/**
 * HTTP-based Query Execution Hook - Editor Only
 * Maintains exact same functionality and flow as original gRPC implementation
 * Maps gRPC steps to HTTP Statement Execution API while preserving all features
 */
import cloneDeep from 'lodash.clonedeep';
import moment from 'moment';
import { useRef } from 'react';
import { useDispatch } from 'react-redux';
import { logout } from '../api';
import { SYSTEM_CATALOG_ROW_LIMIT } from '../constants/systemCatalog';
import { setQSession } from '../reducers/auth/actions';
import { handleQEUpdate } from '../reducers/editor/actions';
import { fetchClusterOptions } from '../reducers/editor_para/thunks';
import {
  AuthenticationService,
  IP_ALLOWLIST_DENIED_MESSAGE,
  isRetryableStatementError,
  statementExecutionService,
} from '../services/statementExecutionService';
import { store } from '../store';
import type { AppDispatch } from '../types/redux';
import { checkInternetConnectivity, sleep, storeLogsInLocalStorage } from '../utils';

interface ColumnDescription {
  name: string;
  fieldType: string;
}

interface FormattedResult {
  [key: string]: unknown;
}

interface FormattedResults {
  results: FormattedResult[];
}

interface QueryState {
  currentCount: number;
  totalCount: number;
  columnData: [number, ColumnDescription[]] | unknown[];
  results: FormattedResult[];
  plannerData: unknown;
  rowCount: number | null;
  fetchedRows: number;
  connection: boolean;
  compiled: boolean;
  executionStarted: boolean;
  metaData: boolean;
  executionStatus: boolean;
  allRecords: boolean;
  explainAnalyse: boolean;
  connectionClosed: boolean;
  statusCallCount: number;
  executeResponse?: StatementResponse;
  finalResponse?: StatementResponse;
  queryID?: string;
  statementId?: string;
}

interface QuerySession {
  sessionID: string;
  engineIP: string;
  queryID: string;
  podIP: string;
  port: number | null;
}

interface ChunkInfo {
  chunk_index: number;
  row_offset: number;
  row_count: number;
  byte_count: number;
}

interface StatementResponse {
  statement_id?: string;
  query_id?: string;
  status?: {
    state: string;
    error?: {
      message?: string;
    };
  };
  result?: {
    chunk_index?: number;
    row_offset?: number;
    row_count?: number;
    data_array?: unknown[][];
  };
  manifest?: {
    schema?: {
      columns?: Array<{
        name: string;
        type_text?: string;
        type_name?: string;
      }>;
    };
    chunks?: ChunkInfo[];
    total_row_count?: number;
  };
}

export interface QueryPayload {
  req: string;
  p: {
    query: string[];
    cluster_uuid: string;
    cluster_name?: string;
    catalog_name: string;
    database: string;
    isLakeformation?: boolean;
    user_email?: string;
    processId?: string;
    /**
     * When true, a failing query does not end a sequential multi-query run —
     * the failure is recorded on that query's result tab and the run moves on
     * to the next query. Set from the user's "Continue on error" preference.
     */
    continueOnError?: boolean;
    [key: string]: unknown;
  };
  traceID: string;
  qAuth?: {
    sessionID: string;
    portId: number | null;
    podip: string;
    created_at: number;
  };
}

interface PodRef {
  [key: string]: {
    podIP: string;
    port: number | null;
    sessionID?: string;
    engineIP?: string;
    queryID?: string;
  };
}

interface RunningRef {
  [key: string]: string;
}

interface DelayThreshold {
  count: number;
  delay: number;
}

interface UseQueryExecutionReturn {
  executeQueries: (
    queryPayload: QueryPayload,
    qState?: QueryState,
    qSession?: QuerySession,
    isNewQuery?: boolean
  ) => Promise<void>;
  abortQuery: (queryPayload: QueryPayload) => Promise<void>;
}

/**
 * HTTP equivalent of formatResults - creates same format as original gRPC
 */
const formatResults = (
  columns: ColumnDescription[] | Record<string, unknown>,
  rows: unknown[][]
): FormattedResults => {
  // Flatten the rows array and map each row to an object
  // columns is now an array of {name, fieldType} to support duplicate column names
  const isArrayFormat = Array.isArray(columns);
  const columnList = isArrayFormat ? columns : Object.keys(columns || {});

  const results = rows.map((row) => {
    const result: FormattedResult = {};
    // Use indexed keys (col_0, col_1, etc.) to preserve duplicate columns
    columnList.forEach((column, index) => {
      const colName = isArrayFormat ? (column as ColumnDescription).name : column;
      // Use indexed key to ensure uniqueness while preserving all columns
      result[`col_${index}`] = row[index];
      // Also store the original column name for display purposes
      result[`__colName_${index}`] = colName;
    });
    return result;
  });

  return { results };
};

/**
 * HTTP equivalent of readRowsFromChunk - processes API data to same format as gRPC
 */
const readRowsFromHTTPChunk = (
  queryColumnsDescription: ColumnDescription[] | Record<string, unknown>,
  dataArray: unknown[][]
): FormattedResults | null => {
  const colLength = Array.isArray(queryColumnsDescription)
    ? queryColumnsDescription.length
    : Object.keys(queryColumnsDescription || {}).length;

  if (!dataArray || dataArray.length === 0) {
    return null;
  }

  // Process rows exactly like original gRPC logic
  const rows: unknown[][] = [];

  // Row-by-row processing (same as original)
  for (let rowIndex = 0; rowIndex < dataArray.length; rowIndex++) {
    const apiRow = dataArray[rowIndex];
    const row: unknown[] = [];

    // Process each column value (same as getSingleValueFromVector).
    // IMPORTANT: use ?? (nullish coalescing), NOT || (logical OR).
    // || coerces ALL falsy values ("", 0, false) to null, which breaks
    // the UI: empty strings display as null, zero displays as null, etc.
    // Users then write IS NOT NULL expecting to filter out empty strings
    // but it doesn't work because the actual data is "", not NULL.
    for (let colIndex = 0; colIndex < colLength; colIndex++) {
      const value = (apiRow as unknown[])[colIndex] ?? null;
      row.push(value);
    }
    rows.push(row);
  }

  // Use exact same formatResults function as original
  const result = formatResults(queryColumnsDescription, rows);

  return result;
};

// Preserve original state structure exactly
const initialQState: QueryState = {
  currentCount: 1,
  totalCount: 1,
  columnData: [],
  results: [],
  plannerData: null,
  rowCount: null,
  fetchedRows: 0,
  // HTTP equivalents of gRPC states
  connection: false, // HTTP: authenticated
  compiled: false, // HTTP: statement submitted
  executionStarted: false, // HTTP: execution started (immediate or polling)
  metaData: false, // HTTP: schema/columns parsed
  executionStatus: false, // HTTP: execution completed
  allRecords: false, // HTTP: all chunks fetched
  explainAnalyse: false, // HTTP: explain-analyze fetched
  connectionClosed: false, // HTTP: cleanup completed
  statusCallCount: 0, // Maintain polling delay logic
};

// Preserve original session structure
const initialQSession: QuerySession = {
  sessionID: '', // HTTP: statement_id
  engineIP: '', // HTTP: warehouse
  queryID: '', // HTTP: statement_id (duplicate for compatibility)
  podIP: '', // HTTP: not needed
  port: null, // HTTP: not needed
};

// Status-poll cadence. Previously the first 10 polls used 0ms delay, which
// hammered the status endpoint at the start of every query and contributed to
// rate-limit failures (PLT-8785). Floor the baseline at 500ms and add bounded
// jitter to avoid synchronized hammering across tabs/users.
export const STATUS_DELAY_THRESHOLDS: DelayThreshold[] = [
  { count: 200, delay: 5000 },
  { count: 150, delay: 4000 },
  { count: 100, delay: 3000 },
  { count: 50, delay: 2000 },
  { count: 10, delay: 1000 },
];

const STATUS_DELAY_BASELINE_MS = 500;
const STATUS_DELAY_JITTER_MS = 200;

export const getStatusDelayBase = (count: number): number => {
  for (const { count: threshold, delay } of STATUS_DELAY_THRESHOLDS) {
    if (count > threshold) return delay;
  }
  return STATUS_DELAY_BASELINE_MS;
};

export const getStatusDelay = (count: number, random: () => number = Math.random): number => {
  const base = getStatusDelayBase(count);
  const jitter = (random() * 2 - 1) * STATUS_DELAY_JITTER_MS;
  return Math.max(0, Math.round(base + jitter));
};

// A single transient status-poll failure must not fail a query that is still
// running fine on the engine. The editor used to hard-fail on the first one, so a
// lone 502 killed a query that went on to complete and land in Query History as a
// success (RCA Q362901-12). Retry the poll a couple of times before giving up;
// genuine failures (4xx, or a FAILED/CANCELED state in the response) are not
// retryable and still surface immediately.
export const STATUS_POLL_MAX_TRANSIENT_RETRIES = 2;
export const STATUS_POLL_RETRY_DELAY_MS = 1000;

/**
 * Poll a statement's status, retrying only transient server-side errors.
 *
 * `isStillRunning` lets the caller abandon the retries when the query is no longer
 * the tab's active one (user cancelled, or a newer run replaced it). Without it a
 * retry could outlive an abort by the backoff window and drive the error path,
 * which paints the tab "Failed" without checking whether the query is still live.
 *
 * Exported for tests.
 */
export const getStatementStatusWithRetry = async (
  statementId: string,
  isStillRunning: () => boolean = () => true
): Promise<StatementResponse> => {
  let lastError: unknown;

  for (let attempt = 0; attempt <= STATUS_POLL_MAX_TRANSIENT_RETRIES; attempt++) {
    try {
      return await statementExecutionService.getStatementStatus(statementId);
    } catch (error) {
      lastError = error;
      const willRetry =
        isRetryableStatementError(error) &&
        attempt < STATUS_POLL_MAX_TRANSIENT_RETRIES &&
        isStillRunning();

      storeLogsInLocalStorage({
        type: willRetry ? 'log' : 'error',
        message: `[STATEMENT_LOG] Status poll failed for ${statementId} (attempt ${
          attempt + 1
        }/${STATUS_POLL_MAX_TRANSIENT_RETRIES + 1}): ${
          (error as Error)?.message
        } — will retry: ${willRetry}`,
      });

      if (!willRetry) break;
      await sleep(STATUS_POLL_RETRY_DELAY_MS);
    }
  }

  throw lastError;
};

async function startInternetCheck(callback: () => Promise<void>): Promise<void | string> {
  return new Promise((resolve) => {
    let retryCount = 0;
    const maxRetries = 10 * 60;
    const retryInterval = setInterval(async () => {
      const isOnline = await checkInternetConnectivity();

      if (isOnline) {
        clearInterval(retryInterval);
        await callback();
        resolve();
      } else if (++retryCount >= maxRetries) {
        clearInterval(retryInterval);
        resolve('timeout');
      }
    }, 1000);
  });
}

/** Map API statement state to UI query status */
const getQueryStatusFromState = (state: string | undefined): string => {
  switch (state) {
    case 'PENDING':
      return 'Connecting with engine...';
    case 'PLANNING':
      return 'Planning...';
    case 'RUNNING':
      return 'Executing...';
    default:
      return 'Executing...';
  }
};

const useQueryExecution = (): UseQueryExecutionReturn => {
  const dispatch = useDispatch<AppDispatch>();
  const podRef = useRef<PodRef>({}); // Maintain for compatibility
  const runningRef = useRef<RunningRef>({});

  /**
   * Create HTTP session - equivalent to gRPC authenticate
   */
  const createSession = async (
    queryPayload: QueryPayload,
    ref_id: string,
    req: string,
    processId: string,
    eventCount: number,
    qState: QueryState,
    qSession: QuerySession
  ): Promise<void> => {
    const totalCount = queryPayload?.p?.query?.length;
    // Use cluster_name (actual name like "sriplay-qs1") for API header, fallback to cluster_uuid
    const clusterName = queryPayload.p.cluster_name || queryPayload.p.cluster_uuid;

    // Set cluster name on service for all subsequent API calls
    statementExecutionService.setClusterName(clusterName);

    // HTTP: Authenticate and get bearer token (only if not already authenticated)
    const currentToken = AuthenticationService.getBearerToken();
    if (!currentToken || currentToken.startsWith('mock-bearer-token')) {
      await AuthenticationService.authenticate(clusterName);
    }

    // Set session info (HTTP equivalent)
    Object.assign(qSession, {
      sessionID: `http-session-${Date.now()}`,
      port: null,
      podIP: '',
      engineIP: queryPayload.p.cluster_uuid,
    });

    podRef.current[ref_id] = {
      podIP: '',
      port: null,
      sessionID: qSession.sessionID,
    };

    // Maintain exact same Redux state updates
    dispatch(
      handleQEUpdate(
        'queriesState',
        {
          isLoading: true,
          isRunning: true,
          queryCounter: `${qState.currentCount}/${totalCount}`,
          requestId: req,
        },
        ref_id,
        '',
        { processId, isEvent: true, ref_id }
      )
    );

    dispatch(
      handleQEUpdate(
        'queriesResults',
        {
          queryStatus: 'Connecting with engine...',
          queryError: '',
          start_time: moment.utc().valueOf(),
          end_time: '',
        },
        ref_id,
        '',
        { processId, eventCount, isEvent: true, ref_id }
      )
    );

    const updatedSession = {
      sessionID: qSession.sessionID,
      portId: null,
      podip: '',
      created_at: moment.utc().valueOf(),
    };
    queryPayload.qAuth = updatedSession;
    dispatch(setQSession(updatedSession));
  };

  const updateQueryState = (
    state: Record<string, unknown>,
    ref_id: string,
    processId: string,
    req: string,
    _eventCount: number,
    extra: Record<string, unknown> = {}
  ): void => {
    dispatch(
      handleQEUpdate(
        'queriesState',
        {
          ...state,
          processId,
          requestId: req,
        },
        ref_id,
        '',
        { processId, isEvent: true, ref_id, ...extra }
      )
    );
  };

  const updateQueryResults = (
    results: Record<string, unknown>,
    ref_id: string,
    processId: string,
    eventCount: number,
    extra: Record<string, unknown> = {}
  ): void => {
    dispatch(
      handleQEUpdate(
        'queriesResults',
        {
          ...results,
        },
        ref_id,
        '',
        { processId, eventCount, isEvent: true, ref_id, ...extra }
      )
    );
  };

  const isRunningQuery = (ref_id: string, processId: string): boolean => {
    return runningRef.current[ref_id] === processId;
  };

  /**
   * System catalog execution — simplified single-request flow via DuckDB.
   * No cluster, no polling, no explain-analyze.
   */
  const executeSystemCatalogQuery = async (queryPayload: QueryPayload): Promise<void> => {
    const { req, p } = queryPayload;
    const splitParts = (req || '').split('_');
    const ref_id = splitParts.length > 0 ? (splitParts[0] ?? '') : '';
    const processId = splitParts.length > 2 ? (splitParts[2] ?? '') : '';
    const totalCount = p?.query?.length ?? 0;
    if (totalCount === 0 || !ref_id) return;

    // Fail-fast if service method is not available
    if (typeof statementExecutionService.executeSystemStatement !== 'function') {
      storeLogsInLocalStorage({
        type: 'error',
        message: '[SYSTEM_CATALOG] executeSystemStatement not available on service',
      });
      return;
    }

    runningRef.current[ref_id] = processId;

    try {
      // Dispatch initial loading state
      updateQueryState(
        { isLoading: true, isRunning: true, queryCounter: `1/${totalCount}` },
        ref_id,
        processId,
        req,
        0
      );
      updateQueryResults(
        {
          queryStatus: 'Executing...',
          queryError: '',
          start_time: moment.utc().valueOf(),
          end_time: '',
        },
        ref_id,
        processId,
        0
      );

      // Execute each query sequentially
      for (let qi = 0; qi < totalCount; qi++) {
        const currentQuery = p.query[qi] ?? '';
        const eventCount = qi;

        if (!isRunningQuery(ref_id, processId)) return;

        const startMs = Date.now();

        const response = await statementExecutionService.executeSystemStatement({
          statement: currentQuery,
          catalog: p.catalog_name,
          schema: p.database,
          rowLimit: SYSTEM_CATALOG_ROW_LIMIT,
        });

        const elapsedMs = Date.now() - startMs;

        if (!isRunningQuery(ref_id, processId)) return;

        // Check for failure
        if (response.status?.state === 'FAILED') {
          const errorMessage = response.status.error?.message || 'Query failed';
          // With continue-on-error the run keeps going, so the remaining
          // queries still count as running until the loop finishes.
          const keepRunning = !!p.continueOnError && qi < totalCount - 1;
          updateQueryState(
            {
              isLoading: false,
              isRunning: keepRunning,
              ...(keepRunning ? { queryCounter: `${qi + 1}/${totalCount}` } : {}),
            },
            ref_id,
            processId,
            req,
            eventCount
          );
          updateQueryResults(
            {
              queryStatus: 'Failed',
              queryError: errorMessage,
              end_time: moment.utc().valueOf(),
            },
            ref_id,
            processId,
            eventCount
          );
          if (!p.continueOnError) return;
          continue;
        }

        // Parse columns
        const schemaColumns = response.manifest?.schema?.columns || [];
        const columnsDescription: ColumnDescription[] = schemaColumns.map((col) => ({
          name: col.name,
          fieldType: col.type_text || col.type_name || 'STRING',
        }));

        // Parse results
        const dataArray = (response.result?.data_array || []) as unknown[][];
        const results = readRowsFromHTTPChunk(columnsDescription, dataArray);

        const rowCount = response.manifest?.total_row_count ?? dataArray.length;
        const truncated = response.manifest?.truncated || false;

        // Dispatch columns
        updateQueryResults(
          { queryStatus: 'Success', columns: columnsDescription },
          ref_id,
          processId,
          eventCount
        );

        await sleep(2);

        // Dispatch results (no explain-analyze, no query plan)
        updateQueryResults(
          {
            queryStatus: 'Success',
            results: results?.results,
            row_count: rowCount,
            execution_time: elapsedMs / 1000,
            end_time: moment.utc().valueOf(),
            queryError: '',
            infoMsg: truncated
              ? `Results limited to ${SYSTEM_CATALOG_ROW_LIMIT.toLocaleString()} rows`
              : '',
          },
          ref_id,
          processId,
          eventCount
        );

        updateQueryState(
          {
            isLoading: false,
            isRunning: qi < totalCount - 1,
            queryCounter: `${qi + 1}/${totalCount}`,
          },
          ref_id,
          processId,
          req,
          eventCount
        );
      }
    } catch (e) {
      const error = e as Error;
      storeLogsInLocalStorage({
        type: 'error',
        message: `[SYSTEM_CATALOG] Error: ${error?.message}`,
      });
      updateQueryState({ isLoading: false, isRunning: false }, ref_id, processId, req, 0);
      updateQueryResults(
        {
          queryStatus: 'Failed',
          queryError: error?.message || 'System catalog query failed',
          end_time: moment.utc().valueOf(),
        },
        ref_id,
        processId,
        0
      );
    } finally {
      delete podRef.current[ref_id];
    }
  };

  /**
   * Main execution function - HTTP version maintaining exact gRPC flow
   */
  const executeQueries = async (
    queryPayload: QueryPayload,
    qState: QueryState = cloneDeep(initialQState),
    qSession: QuerySession = cloneDeep(initialQSession),
    isNewQuery: boolean = true
  ): Promise<void> => {
    const { req, p, traceID } = queryPayload;

    // System catalog queries use a simplified DuckDB-backed execution path
    if (p?.isSystemCatalog) {
      return executeSystemCatalogQuery(queryPayload);
    }

    const isLakeFormation = p?.isLakeformation || false;
    const splitParts = (req || '').split('_');
    const ref_id = splitParts[0] ?? '';
    const processId = splitParts[2] ?? '';

    if (isNewQuery) {
      runningRef.current[ref_id] = processId;
    }

    storeLogsInLocalStorage({
      type: 'log',
      message: `[STATEMENT_LOG] HTTP executeQueries initiated for trace id ${traceID} and req id ${req}`,
    });

    const totalCount = queryPayload?.p?.query?.length;
    const eventCount = qState?.currentCount - 1;

    // Helper: capture query_id from any API response and dispatch to Redux
    const captureQueryId = (response: StatementResponse) => {
      if (response.query_id && !qState.queryID) {
        qState.queryID = response.query_id;
        updateQueryResults({ queryID: response.query_id }, ref_id, processId, eventCount);
        storeLogsInLocalStorage({
          type: 'log',
          message: `[STATEMENT_LOG] Captured query_id from API response: ${response.query_id}`,
        });
      }
    };

    // Helper: reset state for the next query in a sequential run and hand off
    // to it. Shared by the success path and — when the user has turned on
    // "Continue on error" — the failure path, so both chain identically.
    const runNextQuery = async (): Promise<void> => {
      // Reset state for next query (maintain exact original structure)
      const newQState: QueryState = {
        currentCount: qState.currentCount + 1,
        totalCount: queryPayload?.p?.query?.length,
        columnData: [],
        results: [],
        plannerData: null,
        rowCount: null,
        connection: true, // Keep HTTP connection
        compiled: false,
        executionStarted: false,
        metaData: false,
        executionStatus: false,
        allRecords: false,
        explainAnalyse: false,
        connectionClosed: false,
        fetchedRows: 0,
        statusCallCount: 0,
      };
      const newQSession: QuerySession = {
        ...qSession,
        engineIP: p.cluster_uuid,
        queryID: '',
        sessionID: qSession.sessionID, // Preserve session
      };
      await executeQueries(queryPayload, newQState, newQSession, false);
    };

    try {
      // 1. CONNECTION PHASE - HTTP: Authentication
      if (!qState.connection && isRunningQuery(ref_id, processId)) {
        await AuthenticationService.authenticate();
        qState.connection = true;

        updateQueryState(
          {
            isLoading: true,
            queryCounter: `${qState.currentCount}/${totalCount}`,
          },
          ref_id,
          processId,
          req,
          eventCount
        );

        updateQueryResults(
          { queryStatus: 'Connecting with engine...' },
          ref_id,
          processId,
          eventCount
        );
      }

      // 2. SESSION PHASE - HTTP: Create session context
      if (!qSession.sessionID && isRunningQuery(ref_id, processId) && !isLakeFormation) {
        await createSession(queryPayload, ref_id, req, processId, eventCount, qState, qSession);
      } else if (
        !qState.compiled &&
        isRunningQuery(ref_id, processId) &&
        !qState.executionStarted
      ) {
        updateQueryState(
          {
            isLoading: true,
            isRunning: true,
            queryCounter: `${qState.currentCount}/${totalCount}`,
          },
          ref_id,
          processId,
          req,
          eventCount
        );

        updateQueryResults(
          {
            queryStatus: 'Connecting with engine...',
            queryError: '',
            start_time: moment.utc().valueOf(),
            end_time: '',
          },
          ref_id,
          processId,
          eventCount
        );
      }

      // 3. PREPARE PHASE - HTTP: Execute statement (combines prepare + execute)
      if (!qState.compiled && isRunningQuery(ref_id, processId)) {
        const currentQuery = p.query[qState.currentCount - 1] ?? '';

        // HTTP: Execute statement using real v2 API
        const statementResponse = await statementExecutionService.executeStatement({
          warehouse: p.cluster_uuid, // warehouse_id
          catalog: p.catalog_name,
          schema: p.database,
          statement: currentQuery,
          parameters: [], // TODO: Extract from query if needed
          // Same expression as createSession and the poll-only path above, so the
          // three routes into this service can never disagree about the cluster.
          // fetchClusterOptions sets uuid/name/cluster_name to the same QueryService
          // name, so this is the value compute-api validates and Envoy routes on.
          clusterName: p.cluster_name || p.cluster_uuid,
        });

        const statementId = statementResponse.statement_id;
        const engineIP = p.cluster_uuid; // Use cluster as engine

        Object.assign(qState, {
          compiled: true,
          statementId, // Track statement_id separately
          executeResponse: statementResponse, // Store initial response for polling logic
        });
        // qSession.queryID holds statement_id — used for all API calls
        Object.assign(qSession, {
          queryID: statementId,
          engineIP,
          port: null,
          podIP: '',
          sessionID: statementId,
        });

        podRef.current[ref_id] = {
          ...podRef.current[ref_id],
          podIP: '',
          port: null,
          engineIP,
          queryID: statementId,
        };

        // Capture query_id from execute response if available
        captureQueryId(statementResponse);

        updateQueryState(
          {
            isLoading: true,
            queryCounter: `${qState.currentCount}/${totalCount}`,
          },
          ref_id,
          processId,
          req,
          eventCount
        );

        updateQueryResults(
          {
            // Dispatch real query_id if captured from execute response
            ...(qState.queryID ? { queryID: qState.queryID } : {}),
            queryStatus: getQueryStatusFromState(statementResponse.status?.state),
          },
          ref_id,
          processId,
          eventCount
        );
      }

      // 4. EXECUTION START PHASE - HTTP: Handle immediate vs async completion
      if (!qState.executionStarted && isRunningQuery(ref_id, processId)) {
        // Mark execution as started
        Object.assign(qState, { executionStarted: true });
      }

      // 5. STATUS POLLING PHASE - HTTP: Poll until completion (only for PENDING/RUNNING)
      if (!qState.executionStatus && isRunningQuery(ref_id, processId)) {
        // Check if we already have final response from execute call
        const initialResponse = qState.executeResponse || ({} as StatementResponse);

        if (initialResponse.status?.state === 'SUCCEEDED') {
          // Query completed immediately - no need to poll
          qState.executionStatus = true;
          // Use manifest.total_row_count for accurate total, not result.row_count (which is just chunk 0)
          qState.rowCount =
            initialResponse.manifest?.total_row_count ||
            initialResponse.result?.row_count ||
            initialResponse.result?.data_array?.length ||
            0;
          qState.finalResponse = initialResponse;
        } else if (initialResponse.status?.state === 'FAILED') {
          // Query failed immediately - no need to poll
          const errorMessage = initialResponse.status.error?.message || 'Query failed';
          throw new Error(errorMessage);
        } else if (initialResponse.status?.state === 'TIMEDOUT') {
          // Query timed out immediately - no need to poll
          throw new Error(initialResponse.status.error?.message || 'Query Timedout');
        } else if (initialResponse.status?.state === 'CANCELED') {
          // Query cancelled immediately - no need to poll
          throw new Error('Query was cancelled');
        } else if (
          initialResponse.status?.state === 'PENDING' ||
          initialResponse.status?.state === 'PLANNING' ||
          initialResponse.status?.state === 'RUNNING' ||
          !initialResponse.status?.state // Poll-only mode: no executeResponse, poll from scratch
        ) {
          // Query is async - start polling
          storeLogsInLocalStorage({
            type: 'log',
            message: `[STATEMENT_LOG] Entering polling loop: state=${initialResponse.status?.state}, ref_id=${ref_id}`,
          });
          while (!qState.executionStatus && isRunningQuery(ref_id, processId)) {
            qState.statusCallCount = (qState.statusCallCount || 0) + 1;
            const delay = getStatusDelay(qState.statusCallCount);
            if (delay > 0) await sleep(delay);

            // HTTP: Get statement status (transient 5xx retried, see helper)
            const statusResponse = await getStatementStatusWithRetry(qSession.queryID, () =>
              isRunningQuery(ref_id, processId)
            );

            // Guard against missing status field
            if (!statusResponse.status?.state) continue;

            // Capture query_id as soon as it appears in polling response
            captureQueryId(statusResponse);

            // Update UI status based on actual API state
            storeLogsInLocalStorage({
              type: 'log',
              message: `[STATEMENT_LOG] Poll status: ${statusResponse.status.state} → ${getQueryStatusFromState(statusResponse.status.state)}`,
            });
            updateQueryResults(
              { queryStatus: getQueryStatusFromState(statusResponse.status.state) },
              ref_id,
              processId,
              eventCount
            );

            if (statusResponse.status.state === 'SUCCEEDED') {
              qState.executionStatus = true;
              // Use manifest.total_row_count for accurate total, not result.row_count (which is just chunk 0)
              qState.rowCount =
                statusResponse.manifest?.total_row_count ||
                statusResponse.result?.row_count ||
                statusResponse.result?.data_array?.length ||
                0;
              qState.finalResponse = statusResponse;
            } else if (statusResponse.status.state === 'FAILED') {
              const errorMessage =
                statusResponse.status.error?.message || 'Query failed during execution';
              throw new Error(errorMessage);
            } else if (statusResponse.status.state === 'CANCELED') {
              throw new Error('Query was cancelled');
            } else if (statusResponse.status.state === 'TIMEDOUT') {
              throw new Error(statusResponse.status.error?.message || 'Query Timedout');
            }
            // Continue polling if still PENDING, PLANNING, or RUNNING
          }
        } else {
          // Unexpected state
          qState.executionStatus = true; // Skip polling
          qState.finalResponse = initialResponse;
        }
      }

      // 6. RESULT FETCHING PHASE - HTTP: Get result data and chunks
      //    Explain analyze is kicked off in parallel after the first chunk is processed
      if (!qState.allRecords && isRunningQuery(ref_id, processId)) {
        updateQueryState(
          {
            isRunning: false,
            queryCounter: `${qState.currentCount}/${totalCount}`,
          },
          ref_id,
          processId,
          req,
          eventCount
        );

        updateQueryResults(
          {
            queryStatus: 'Fetching results...',
            end_time: moment.utc().valueOf(),
          },
          ref_id,
          processId,
          eventCount
        );

        // Process results and chunks (equivalent to getNextResultBatch loop)
        const finalResponse = qState.finalResponse;

        // Parse schema (equivalent to getResultMetadata + getQueryColumnsInfo)
        if (!qState.metaData && isRunningQuery(ref_id, processId)) {
          // Replicate exact getQueryColumnsInfo format
          const schemaColumns = finalResponse?.manifest?.schema?.columns || [];
          const rowCount =
            finalResponse?.manifest?.total_row_count || finalResponse?.result?.row_count || 0;

          // Transform to match exact getQueryColumnsInfo output format
          const columnsDescription: ColumnDescription[] = [];

          for (let i = 0; i < schemaColumns.length; i++) {
            const col = schemaColumns[i];
            if (!col) continue;
            const name = col.name;
            const fieldType = col.type_text || col.type_name || 'STRING';

            // Skip invalid columns (same as original logic)
            if (!(name || fieldType)) {
              continue;
            }

            // Use exact same format as getQueryColumnsInfo
            columnsDescription.push({ name, fieldType });
          }

          // Return exact same format as getQueryColumnsInfo: [rowCount, columnsDescription]
          const columnInfo: [number, ColumnDescription[]] = [Number(rowCount), columnsDescription];

          Object.assign(qState, { metaData: true, columnData: columnInfo });
        }

        // Derive total chunks from manifest.chunks array (more reliable than total_chunk_count)
        const manifestChunks = finalResponse?.manifest?.chunks || [];
        const totalChunks = manifestChunks.length || 1;
        // Start from the chunk_index in the initial result (chunk 0)
        let currentChunkIndex = finalResponse?.result?.chunk_index ?? 0;

        // Promise for explain analyze - kicked off after first chunk, resolved after all chunks
        let explainAnalyzePromise: Promise<unknown> | null = null;

        // Process chunks - chunk 0 is in initial response, chunks 1+ need to be fetched
        while (!qState.allRecords && isRunningQuery(ref_id, processId)) {
          let resultBatch: unknown[][];

          if (currentChunkIndex === (finalResponse?.result?.chunk_index ?? 0)) {
            // First chunk - use data from final response
            resultBatch = (finalResponse?.result?.data_array || []) as unknown[][];
          } else {
            // Additional chunks - fetch from chunk API
            if (currentChunkIndex >= totalChunks) {
              // No more chunks available
              qState.allRecords = true;
              break;
            }

            try {
              const chunkResponse = await statementExecutionService.getResultChunk(
                qSession.queryID,
                currentChunkIndex
              );
              resultBatch = (chunkResponse.data_array || []) as unknown[][];

              // If empty response, we're done
              if (!resultBatch || resultBatch.length === 0) {
                qState.allRecords = true;
                break;
              }
            } catch (chunkError) {
              // An IP-allowlist denial is a policy decision, not a transient
              // chunk failure. Rethrow so the outer catch surfaces the friendly
              // message; swallowing it here would report the query as Success
              // with silently truncated rows.
              if ((chunkError as Error)?.message === IP_ALLOWLIST_DENIED_MESSAGE) {
                throw chunkError;
              }
              // Log error but continue - don't silently break
              storeLogsInLocalStorage({
                type: 'error',
                message: `[STATEMENT_LOG] Error fetching chunk ${currentChunkIndex}: ${(chunkError as Error)?.message}`,
              });
              // Mark as all records if we can't fetch more
              qState.allRecords = true;
              break;
            }
          }

          // Process results exactly like gRPC version using readRowsFromHTTPChunk
          const columnData = qState.columnData as [number, ColumnDescription[]];
          const results = readRowsFromHTTPChunk(columnData?.[1], resultBatch);

          const isEmptyBuffer = !results?.results?.length;
          const updatedCount = qState?.fetchedRows + (results?.results?.length || 0);

          // Apply 10k limit (maintain existing logic)
          if (updatedCount > 10000 && results) {
            results.results = (results?.results || []).slice(0, 10000 - qState?.fetchedRows);
          }

          qState.fetchedRows = updatedCount;
          currentChunkIndex++; // Next chunk = current index + 1

          const isAllRecords =
            qState?.fetchedRows === qState.rowCount ||
            qState?.fetchedRows >= 10000 ||
            isEmptyBuffer ||
            currentChunkIndex >= totalChunks;

          qState.allRecords = isAllRecords;

          if (!isAllRecords) {
            updateQueryState(
              {
                isRunning: qState.currentCount !== totalCount,
                queryCounter: `${qState.currentCount}/${totalCount}`,
              },
              ref_id,
              processId,
              req,
              eventCount
            );
          }

          // Update results - maintain exact same pattern as original gRPC
          const columnsData = columnData?.[1];

          const columnsDispatch = {
            queryStatus: isAllRecords ? 'Success' : 'Fetching results...',
            columns: columnsData,
          };

          updateQueryResults(columnsDispatch, ref_id, processId, eventCount);

          await sleep(2); // Maintain original delay

          const resultsDispatch = {
            queryStatus: isAllRecords ? 'Success' : 'Fetching results...',
            results: results?.results,
          };

          updateQueryResults(resultsDispatch, ref_id, processId, eventCount);

          // After the first chunk is processed, kick off explain analyze in parallel
          if (currentChunkIndex === 1 && !explainAnalyzePromise && !qState.explainAnalyse) {
            explainAnalyzePromise = statementExecutionService
              .getExecutionStats(qSession.queryID)
              .catch((err) => {
                storeLogsInLocalStorage({
                  type: 'error',
                  message: `[STATEMENT_LOG] Parallel explain analyze error: ${(err as Error)?.message}`,
                });
                return null;
              });
          }
        }

        // 7. EXPLAIN ANALYZE PHASE - await the parallel call started after first chunk
        if (!qState.explainAnalyse && isRunningQuery(ref_id, processId)) {
          try {
            const explainResponse = explainAnalyzePromise
              ? await explainAnalyzePromise
              : await statementExecutionService.getExecutionStats(qSession.queryID);

            Object.assign(qState, { explainAnalyse: true });

            const plannerData = (explainResponse as { explainAnalyze: string })?.explainAnalyze;
            const data = JSON.parse(plannerData);
            const {
              engine_type,
              output_rows,
              row_count_out,
              total_query_time,
              parsingTime,
              executionQueueingTime,
              is_cached = false,
              parsing_time = 0,
              queryId,
            } = data || {};
            // Use output_rows for Native engine, row_count_out for legacy
            const rowcount = engine_type === 'Native' ? output_rows : row_count_out;
            // Only use explain-analyze queryId as fallback if not already captured
            const resolvedQueryId =
              qState.queryID || queryId || (data?.plan as Record<string, unknown>)?.query_id;
            if (resolvedQueryId && !qState.queryID) {
              qState.queryID = resolvedQueryId as string;
            }

            updateQueryState(
              {
                isLoading: false,
                queryCounter: `${qState.currentCount}/${totalCount}`,
              },
              ref_id,
              processId,
              req,
              eventCount
            );

            updateQueryResults(
              {
                row_count: rowcount,
                execution_time: (is_cached ? 0 : total_query_time) / 1000,
                parsingTime: (is_cached ? parsing_time : parsingTime) / 1000,
                queuingTime: executionQueueingTime / 1000,
                plan: data,
                isCached: is_cached,
                queryPlanner: data,
                run_by: p?.user_email,
                queryID: resolvedQueryId,
                queryError: '',
                infoMsg:
                  rowcount > 10000 ? 'Maximum record count shown on UI are limited to 10,000' : '',
                queryStatus: 'Success',
              },
              ref_id,
              processId,
              eventCount
            );
          } catch {
            Object.assign(qState, { explainAnalyse: true });
          }
        }
      }

      // 8. CLEANUP PHASE — call clearOrCancel so the planner releases QueryContext and the
      // executor drops its chunks immediately, instead of waiting for the server's 1-hour TTL.
      // This is the HTTP v2 analog of JDBC's clearOrCancelQuery RPC. Fire-and-forget: the
      // service layer already swallows errors (cleanup is best-effort; user has their results).
      if (!qState.connectionClosed && isRunningQuery(ref_id, processId)) {
        if (qSession.queryID) {
          statementExecutionService.clearOrCancelStatement(qSession.queryID).catch((err) => {
            storeLogsInLocalStorage({
              type: 'error',
              message: `[STATEMENT_LOG] clearOrCancel failed (non-fatal): ${(err as Error)?.message}`,
            });
          });
        }
        qState.connectionClosed = true;
      }

      // 9. MULTI-QUERY SUPPORT - Execute next query if exists
      if (qState.currentCount === totalCount) {
        delete podRef.current[ref_id];
        return;
      } else {
        await runNextQuery();
      }
    } catch (e) {
      const error = e as Error & { code?: number };
      storeLogsInLocalStorage({
        type: 'log',
        message: `[STATEMENT_LOG] Error for trace id ${traceID}: ${error?.message} code ${error?.code}`,
      });

      // Handle authentication errors
      if ((error?.message || '').toLowerCase().includes('unauthorized user')) {
        await logout();
        return;
      }

      // Check for offline status
      const isOnline = await checkInternetConnectivity();
      if (!isOnline) {
        const result = await startInternetCheck(async () => {
          await executeQueries(queryPayload, qState, qSession, false);
        });

        if (result !== 'timeout') return;

        if (!(await checkInternetConnectivity())) {
          updateQueryState(
            {
              errorTxt: {
                err: 'You are offline, Please check your network connection',
                counter: `${qState.currentCount}/${totalCount}`,
              },
              isLoading: false,
              isRunning: false,
              processError: 'You are offline, Please check your network connection',
            },
            ref_id,
            processId,
            req,
            eventCount
          );

          updateQueryResults(
            {
              queryError: 'You are offline, Please check your network connection',
              errorTxt: {
                err: 'You are offline, Please check your network connection',
                counter: `${qState.currentCount}/${totalCount}`,
              },
              start_time: '',
              queryID: qState.queryID || '',
              queryStatus: 'Failed',
            },
            ref_id,
            processId,
            eventCount
          );
          delete podRef.current[ref_id];
        }
        return;
      }

      // IP-allowlist denial (Envoy RBAC 403): a policy decision, not a
      // cluster-health problem. Must be checked BEFORE the cluster branch
      // below — the friendly message contains the word "cluster", and the
      // substring match would otherwise show a start-cluster quick action
      // for something no cluster restart can fix.
      if (error?.message === IP_ALLOWLIST_DENIED_MESSAGE) {
        updateQueryState(
          {
            isLoading: false,
            isRunning: false,
            queryStatus: 'Failed',
            processError: error.message,
          },
          ref_id,
          processId,
          req,
          eventCount
        );
        updateQueryResults(
          { queryError: error.message, queryStatus: 'Failed' },
          ref_id,
          processId,
          eventCount
        );
        delete podRef.current[ref_id];
        return;
      }

      // Handle cluster errors with quick action (maintain existing UX)
      if (
        error?.message?.toLowerCase().includes('cluster') ||
        error?.message?.toLowerCase().includes('warehouse')
      ) {
        const editorState = store.getState()?.editor as { queriesStats?: Record<string, unknown> };
        const error_cluster_info = editorState?.queriesStats?.[ref_id];

        try {
          await new Promise<void>((resolve, reject) => {
            dispatch(
              fetchClusterOptions(
                false,
                (() => resolve()) as unknown as (value: unknown) => void,
                reject
              ) as unknown as Parameters<typeof dispatch>[0]
            );
          });
        } catch {
          // Silent fail for cluster options fetch
        }

        updateQueryState(
          {
            isLoading: false,
            isRunning: false,
            queryStatus: 'Failed',
            processError: '',
            clusterError: {
              message: error?.message,
              clusterStatus: 'Deleting',
              error_cluster_info,
              showQuickAction: true,
              originalPayload: queryPayload,
            },
          },
          ref_id,
          processId,
          req,
          eventCount
        );

        updateQueryResults({ queryStatus: 'Failed' }, ref_id, processId, eventCount);

        delete podRef.current[ref_id];
        return;
      }

      // Continue-on-error: this query failed, but the user asked for the rest of
      // the run to go ahead. Record the failure against this query's own result
      // tab and hand off to the next query.
      //
      // Deliberately does NOT set processError — that is the editor-level "the
      // whole run stopped" banner, and ResultErrors uses it to suppress the
      // "Executing..." spinner on every other tab.
      //
      // The isRunningQuery() guard is what keeps an abort winning over this:
      // abortQuery clears runningRef, so a CANCELED state thrown mid-poll can
      // never be mistaken for a query error and silently resume the run. The
      // offline / unauthorized / cluster branches above return before reaching
      // here for the same reason — every remaining query would fail identically.
      if (
        p.continueOnError &&
        qState.currentCount < totalCount &&
        isRunningQuery(ref_id, processId)
      ) {
        // Always leave a non-empty message on the tab: the run moves on, so an
        // empty queryError would render a failed query as a blank panel.
        const queryErrorMessage =
          error?.message || 'Something went wrong! Please contact e6data support.';

        updateQueryState(
          {
            isLoading: false,
            isRunning: true,
            queryCounter: `${qState.currentCount}/${totalCount}`,
          },
          ref_id,
          processId,
          req,
          eventCount
        );

        updateQueryResults(
          {
            queryError: queryErrorMessage,
            errorTxt: {
              err: queryErrorMessage,
              counter: `${qState.currentCount}/${totalCount}`,
            },
            // start_time is left as-is (unlike the terminal error path below) so
            // the failed query still reports how long it ran for.
            end_time: moment.utc().valueOf(),
            queryID: qState.queryID || '',
            queryStatus: 'Failed',
          },
          ref_id,
          processId,
          eventCount
        );

        await runNextQuery();
        return;
      }

      // Generic error handling
      const errorMessage = !error?.code
        ? 'Something went wrong! Please contact e6data support.'
        : error?.message || 'Something went wrong! Please contact e6data support.';

      updateQueryState(
        {
          errorTxt: {
            err: error?.message,
            counter: `${qState.currentCount}/${totalCount}`,
          },
          isLoading: false,
          isRunning: false,
          processError: errorMessage,
        },
        ref_id,
        processId,
        req,
        eventCount
      );

      updateQueryResults(
        {
          queryError: error?.message,
          errorTxt: {
            err: error?.message,
            counter: `${qState.currentCount}/${totalCount}`,
          },
          start_time: '',
          queryID: qState.queryID || '',
          queryStatus: 'Failed',
        },
        ref_id,
        processId,
        eventCount
      );

      delete podRef.current[ref_id];
    } finally {
      if (runningRef?.current?.[ref_id] && runningRef.current[ref_id] === processId) {
        delete runningRef?.current?.[ref_id];
      }

      storeLogsInLocalStorage({
        type: 'log',
        message: `[STATEMENT_LOG] Query execution completed for ${traceID} and req id ${req}`,
      });

      // HTTP: Cleanup (no connection to close)
    }
  };

  /**
   * Abort query - HTTP version maintaining exact same interface
   */
  const abortQuery = async (queryPayload: QueryPayload): Promise<void> => {
    const { req, traceID, p } = queryPayload || {};

    storeLogsInLocalStorage({
      type: 'log',
      message: `[STATEMENT_LOG] Abort query initiated for trace id ${traceID} and req id ${req}`,
    });

    try {
      const ref_id = (req || '').split('_')[0] ?? '';
      const sessionData = podRef.current[ref_id];

      if (ref_id && runningRef?.current?.[ref_id] && runningRef.current[ref_id] === p.processId) {
        delete runningRef.current[ref_id];
      }

      // Use queryID from podRef session data, fall back to query_id from abort payload
      const statementId = sessionData?.queryID || (p?.query_id as string);

      if (statementId) {
        // HTTP: Cancel statement
        await statementExecutionService.cancelStatement(statementId);
      } else {
        storeLogsInLocalStorage({
          type: 'log',
          message: `[STATEMENT_LOG] No statement ID available to cancel for ref_id ${ref_id}. sessionData: ${JSON.stringify(sessionData)}, p.query_id: ${p?.query_id}`,
        });
      }
    } catch (e) {
      const error = e as Error & { code?: number };
      storeLogsInLocalStorage({
        type: 'log',
        message: `[STATEMENT_LOG] Error aborting query trace id ${traceID}: ${error?.message} code ${error?.code}`,
      });
    } finally {
      storeLogsInLocalStorage({
        type: 'log',
        message: `[STATEMENT_LOGS] Query abort completed for ${traceID} and req id ${req}`,
      });
    }
  };

  // Maintain exact same interface as original
  return { executeQueries, abortQuery };
};

export default useQueryExecution;
